import { LineEdit } from '@qr-front/icons/line-edit'
import { LineLinkAlt01 } from '@qr-front/icons/line-link-alt-01'
import { LineLock } from '@qr-front/icons/line-lock'
import { LinePauseCircle } from '@qr-front/icons/line-pause-circle'
import { SolidCircle } from '@qr-front/icons/solid-circle'
import { Tooltip } from '@qr-front/ui/components/Tooltip'
import { cn } from '@qr-front/ui/utils'
import {
	nameSchema,
	urlSchema,
} from 'features/generator/contentFormSections/fieldSchemas'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { useContext, useRef } from 'react'
import * as yup from 'yup'

import { getQrTypeTranslation, QR_TYPE } from '@/generator/shared'
import { TTranslationFunction } from '@/types'

import { useFormatDate } from '../../../../../../hooks/dateFormattingHooks'
import { addMissingUrlProtocol } from '../../../../../../utils/urlUtils'
import { TUrlContent } from '../../../../../generator/qrTypes/Url/WebsiteUrlContentState'
import {
	createdColDef,
	disabledColDef,
	nameColDef,
	typeColDef,
} from '../columnsConfig'
import {
	editQrNameDialogAtom,
	editQrNameDialogAtomData,
	editQrUrlDialogAtom,
	editQrUrlDialogDataAtom,
} from '../dialogsState'
import { withExpiredDialogIfNotAllowed } from '../TableActions/Actions/tableActionUtils'
import { rowContext, tableAtom } from '../tableState'
import { useCellValue } from '../utils/cellValue'

export const nameValidationSchema = (t: TTranslationFunction) =>
	yup.object({
		value: nameSchema(t, { required: true }),
	})

export const urlValidationSchema = (t: TTranslationFunction) =>
	yup.object({
		value: urlSchema(t, { required: true }),
	})

export const PasswordLock = () => {
	const t = useTranslations()
	const row = useContext(rowContext)
	const anchorRef = useRef<HTMLSpanElement>(null)

	if (
		!row.original.hasPassword ||
		row.original.content.type === QR_TYPE.Wifi
	) {
		return null
	}

	return (
		<>
			<span
				ref={anchorRef}
				role="img"
				aria-label={t(
					'dashboard__qr_table__qr_card__password_lock_tooltip',
				)}
				className="flex shrink-0 text-grey"
			>
				<LineLock className="size-4" />
			</span>
			<Tooltip
				anchorRef={anchorRef}
				delay={100}
				position="top"
				visual="private"
			>
				{t('dashboard__qr_table__qr_card__password_lock_tooltip')}
			</Tooltip>
		</>
	)
}

export const Name = ({ className }: { className?: string } = {}) => {
	const t = useTranslations()
	const row = useContext(rowContext)
	const name = useCellValue(nameColDef)
	const table = useAtomValue(tableAtom)
	const setOpenEditQrNameDialog = useSetAtom(editQrNameDialogAtom)
	const setEditQrNameDialogData = useSetAtom(editQrNameDialogAtomData)

	const handleClickEdit = withExpiredDialogIfNotAllowed(() => {
		setEditQrNameDialogData({
			table,
			row,
			name,
		})
		setOpenEditQrNameDialog(true)
	})

	return (
		<div className="flex w-0 shrink grow items-center gap-2 tablet:w-auto">
			<h4
				className={cn(
					'type-body-input-title shrink truncate tablet:type-heading-4',
					className,
				)}
			>
				{name || t('dashboard__qr_name__untitled')}
			</h4>
			<PasswordLock />
			<button onClick={handleClickEdit}>
				<LineEdit className="size-4 text-grey" />
			</button>
		</div>
	)
}

export const Type = () => {
	const t = useTranslations()
	const type = useCellValue(typeColDef)

	const translatedType = getQrTypeTranslation(t)(type)

	return (
		<span className="type-body-small text-grey">
			{t.rich('dashboard__qr_table__qr_card__type', {
				name: () => <span className="text-black">{translatedType}</span>,
			})}
		</span>
	)
}

export const Status = ({ className }: { className?: string }) => {
	const t = useTranslations()

	const isDisabled = useCellValue(disabledColDef)

	return (
		<span
			className={cn(
				'type-body-small flex items-center gap-2 font-medium',
				isDisabled ? 'text-grey' : 'text-green',
				className,
			)}
		>
			{isDisabled ? (
				<LinePauseCircle className="size-4" />
			) : (
				<SolidCircle className="size-2" />
			)}
			{isDisabled
				? t('dashboard__qr_table__qr_card__paused')
				: t('dashboard__qr_table__qr_card__active')}
		</span>
	)
}

export const CreateDate = () => {
	const t = useTranslations()

	const date = useCellValue(createdColDef)
	const formattedDate = useFormatDate()(date)

	return (
		<span className="type-body-small">
			{t('dashboard__qr_table__qr_card__creation_date', {
				date: formattedDate,
			})}
		</span>
	)
}

export const WebsiteUrl = ({ className }: { className?: string } = {}) => {
	const type = useCellValue(typeColDef)
	const row = useContext(rowContext)
	const table = useAtomValue(tableAtom)
	const setOpenEditUrlDialog = useSetAtom(editQrUrlDialogAtom)
	const setEditUrlDialogData = useSetAtom(editQrUrlDialogDataAtom)

	if (type !== QR_TYPE.Url) {
		return null
	}

	const handleClickEdit = withExpiredDialogIfNotAllowed(() => {
		setEditUrlDialogData({
			table,
			row,
			url,
		})
		setOpenEditUrlDialog(true)
	})

	const content = row.original.content as TUrlContent

	const url = content.url

	return (
		<div className="type-body-small flex min-w-0 items-center overflow-hidden text-dark-grey">
			<a
				href={addMissingUrlProtocol(url)}
				target="_blank"
				rel="noreferrer"
				className={cn(
					'flex min-w-0 shrink items-center gap-1 overflow-hidden',
					className,
				)}
			>
				<LineLinkAlt01 className="size-4 shrink-0" />
				<span className="min-w-0 truncate">{url}</span>
			</a>
			<button className="ml-2 shrink-0" onClick={handleClickEdit}>
				<LineEdit className="size-4 text-grey" />
			</button>
		</div>
	)
}

export const VerticalSeparator = ({ className }: { className?: string }) => (
	<div className={cn('h-full w-px bg-border-grey-50', className)} />
)
